import { useMemo } from 'react';
import { Box, Typography, Stack, Chip, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { thresholdExceedance, filterByWindow } from '../statistics';
import { useTimelineControls } from '../TimelineContext';
import { getSensorType } from '../../../../services/localizationService';

export default function ExceedanceWidget({ recordings = [], rules = [], activeRules = [], widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const recId = widgetConfig?.recId || '';

  const rec = recordings.find((r) => r.id === recId);

  const results = useMemo(() => {
    if (!rec?.data || activeRules.length === 0) return null;

    const dataKeys = Object.keys(rec.data);
    const allResults = [];

    for (const rule of activeRules) {
      const ruleSensorType = getSensorType(rule.sensorType);
      const matchingKeys = dataKeys.filter(
        (key) => getSensorType(key.split(':').pop()) === ruleSensorType
      );

      for (const key of matchingKeys) {
        let points = rec.data[key];
        if (timeWindow) points = filterByWindow(points, timeWindow.start, timeWindow.end);

        const exceedances = thresholdExceedance(points, [rule]);
        if (exceedances.length > 0) {
          allResults.push({
            ...exceedances[0],
            sensorKey: key,
            sensorName: friendlyNameConverter(key),
          });
        }
      }
    }

    return allResults;
  }, [rec, activeRules, timeWindow]);

  const totalDuration = rec ? (
    timeWindow ? (timeWindow.end - timeWindow.start) : (rec.endTime - rec.startTime)
  ) : 0;
  const totalMinutes = totalDuration / 60000;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {activeRules.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Activate rules first (use the Rules panel)</Typography>
        </Box>
      ) : results && results.length > 0 ? (
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
            <Chip label={`${activeRules.length} rules`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            <Chip label={`Total: ${totalMinutes.toFixed(1)} min`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Rule</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Sensor</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">Time</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">%</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">Events</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r, i) => {
                const pct = totalMinutes > 0 ? (r.totalMinutes / totalMinutes) * 100 : 0;
                return (
                  <TableRow key={i}>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>{r.ruleName}</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>{r.sensorName}</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: r.totalMinutes > 0 ? 'error.main' : 'text.secondary' }} align="right">
                      {r.totalMinutes.toFixed(1)} min
                    </TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: pct > 10 ? 'error.main' : 'text.secondary' }} align="right">
                      {pct.toFixed(1)}%
                    </TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{r.exceedanceCount}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {rec ? 'No exceedances detected' : 'Select a recording'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
